import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Linkedin, User, ArrowUpRight, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface LeadershipMember {
  id: string;
  name: string;
  position: string;
  level: 'general' | 'executive' | 'manager';
  description?: string;
  descriptionKey?: string;
  linkedinUrl?: string;
  imageUrl?: string;
  imagePosition?: string;
}

const Leadership = () => {
  const { t } = useLanguage();
  const [selectedMember, setSelectedMember] = useState<LeadershipMember | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  const leadershipTeam: LeadershipMember[] = [
    { id: '1', name: 'Andrej Janotka', position: 'General Manager', level: 'general',
      description: 'Andrej bol vymenovaný za pozíciu General Manager Artifex Systems Slovakia v decembri 2025. V skupine pôsobí viac ako 20 rokov v rôznych rolách v Program Management, Engineering a Operations.\n\nPrináša s sebou desaťročia skúseností v automobilovom sektore a bohatú expertízu v kontinuálnom zlepšovaní, inžinierstve a čo je najdôležitejšie, v rozvoji ľudí a tímov.\n\nPod jeho vedením prebehla úspešná transformácia závodu v Lozorne ako súčasti Artifex Group, čím sa dosiahla stabilizácia operačného a finančného výkonu.\n\nAndrej má titul z materiálového inžinierstva zo Slovenskej technickej univerzity.',
      linkedinUrl: 'https://www.linkedin.com/in/andrej-janotka', imageUrl: '/team/andrej-janotka.jpg' },
    { id: '2', name: 'Peter Jurčišin', position: 'Lead Business Manager', level: 'executive',
      description: 'Peter Jurčišin je Lead Business Manager v Artifex, ktorý prináša viac ako 15 rokov skúseností v automobilovom sektore so silným zameraním na obchodné vedenie a rozvoj zákazníkov. Kombinuje hlbokú expertízu v obchodnom manažmente, predaji, vzťahoch so zákazníkmi so solídnym technickým základom v strojárstve, čo umožňuje efektívnu rovnováhu medzi obchodnou stratégiou a technickým porozumením.\n\nOd kedy sa pripojil k závodu Artifex Lozorno pred piatimi rokmi, Peter viedol a podporoval obchodné aktivity pre prémiové OEM programy, vrátane Land Rover a Jaguar, ako aj Ford, s zodpovednosťou za programy interiérových komponentov. Vo svojej súčasnej funkcii dohliada na zapojenie zákazníkov, obchodný výkon a medzifunkčnú koordináciu, prispievajúc k úspešnej realizácii programov, dlhodobým partnerstvám so zákazníkmi a udržateľnému obchodnému rastu.',
      linkedinUrl: 'https://www.linkedin.com/in/peter-jurcisin', imageUrl: '/team/peter-jurcisin.jpg' },
    { id: '3', name: 'Andrea Rebejová', position: 'HR & HSE Manager', level: 'executive',
      description: 'Andrea Rebejová je HR & HSE Manager zodpovedná za ľudské zdroje a bezpečnosť a ochranu zdravia pri práci.',
      linkedinUrl: 'https://www.linkedin.com/in/andrea-rebejova', imageUrl: '/team/andrea-rebejova.jpg' },
    { id: '4', name: 'Lucia Oreska', position: 'Finance Manager', level: 'executive',
      descriptionKey: 'leadership.team.lucia.description',
      linkedinUrl: 'https://www.linkedin.com/in/lucia-oreska', imageUrl: '/team/lucia-oreska.jpg' },
    { id: '5', name: 'Marek Spál', position: 'IT Manager', level: 'executive',
      descriptionKey: 'leadership.team.marekSpal.description',
      linkedinUrl: 'https://www.linkedin.com/in/marek-spal', imageUrl: '/team/marek-spal.jpg' },
    { id: '6', name: 'Miroslav Kordoš', position: 'Quality Manager', level: 'manager',
      descriptionKey: 'leadership.team.miroslav.description',
      linkedinUrl: 'https://www.linkedin.com/in/miroslav-kordos', imageUrl: '/team/miroslav-kordos.jpg' },
    { id: '7', name: 'Pavol Hodor', position: 'Technical Manager', level: 'manager',
      descriptionKey: 'leadership.team.pavol.description',
      linkedinUrl: 'https://www.linkedin.com/in/pavol-hodor', imageUrl: '/team/pavol-hodor.jpg' },
    { id: '8', name: 'Jozef Bureš', position: 'Production Manager', level: 'manager',
      descriptionKey: 'leadership.team.jozef.description',
      linkedinUrl: 'https://www.linkedin.com/in/jozef-bures', imageUrl: '/team/jozef-bures.jpg' },
    { id: '9', name: 'Marek Lipský', position: 'Supply Chain Manager', level: 'manager',
      descriptionKey: 'leadership.team.marek.description',
      linkedinUrl: 'https://www.linkedin.com/in/marek-lipsky', imageUrl: '/team/marek-lipsky.jpg' },
    { id: '10', name: 'Róber Báleš', position: 'Maintenance Manager', level: 'manager',
      descriptionKey: 'leadership.team.robert.description',
      linkedinUrl: 'https://www.linkedin.com/in/rober-bales', imageUrl: '/team/robert-bales.png' },
    { id: '11', name: 'Róbert Karnoš', position: 'Procurement Manager', level: 'manager',
      descriptionKey: 'leadership.team.robertKarnos.description',
      imageUrl: '/team/robert-karnos.png', imagePosition: 'center 18%' },
  ];

  const lead = leadershipTeam.filter(m => m.id === '1');
  const executives = leadershipTeam.filter(m => ['2', '3', '4', '5', '11'].includes(m.id));
  const managers = leadershipTeam.filter(m => ['6', '7', '8', '9', '10'].includes(m.id));

  const PortraitCard = ({ member, size = 'md' }: { member: LeadershipMember; size?: 'lg' | 'md' }) => {
    const hasImage = member.imageUrl && !imageErrors.has(member.id);
    return (
      <button
        onClick={() => setSelectedMember(member)}
        className="group text-left w-full"
      >
        <div className={`relative overflow-hidden bg-muted/50 border border-[hsl(var(--hairline))] ${size === 'lg' ? 'aspect-[4/5]' : 'aspect-[4/5]'} mb-4 rounded-md`}>
          {hasImage ? (
            <img
              src={member.imageUrl}
              alt={member.name}
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-[1.02]"
              style={{ objectPosition: member.imagePosition ?? 'center' }}
              onError={() => setImageErrors(prev => new Set(prev).add(member.id))}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-primary/5">
              <User className="w-16 h-16 text-primary/30" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/0 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-primary" />
            </div>
          </div>
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <h3 className={`font-display font-semibold text-primary group-hover:text-secondary transition-colors ${size === 'lg' ? 'text-2xl' : 'text-lg'}`}>
            {member.name}
          </h3>
        </div>
        <p className={`text-muted-foreground mt-1 ${size === 'lg' ? 'text-base' : 'text-sm'}`}>
          {member.position}
        </p>
      </button>
    );
  };

  return (
    <section id="leadership" className="section-pad bg-background">
      <div className="container-tight">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <span className="eyebrow mb-6 block">{t('leadership.badge')}</span>
            <h2 className="font-display font-bold text-primary" style={{ fontSize: 'clamp(2.25rem, 4vw, 3.5rem)', lineHeight: 1.02 }}>
              {t('leadership.title')}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            viewport={{ once: true }}
            className="lg:col-span-6 lg:col-start-7 lede pt-2"
          >
            {t('leadership.description')}
          </motion.p>
        </div>

        {/* Lead — feature */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20"
        >
          {lead.map(m => (
            <div key={m.id} className="lg:col-span-5">
              <PortraitCard member={m} size="lg" />
            </div>
          ))}
          <div className="lg:col-span-6 lg:col-start-7 pt-2 self-center">
            <span className="section-num mb-3 block">— {t('sub.gm')}</span>
            <blockquote className="font-display text-2xl md:text-3xl text-primary leading-snug">
              “Artifex stands for collaborative innovation — bringing people together toward a shared purpose.”
            </blockquote>
          </div>
        </motion.div>

        <div className="hairline mb-20" />

        {/* Executive line */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="flex items-end justify-between mb-10">
            <span className="section-num">— {t('sub.executives')}</span>
            <div className="h-px flex-1 ml-10 bg-[hsl(var(--hairline))] mb-2" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8">
            {executives.map(m => (
              <PortraitCard key={m.id} member={m} />
            ))}
          </div>
        </motion.div>

        {/* Managers */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="flex items-end justify-between mb-10">
            <span className="section-num">— {t('sub.management')}</span>
            <div className="h-px flex-1 ml-10 bg-[hsl(var(--hairline))] mb-2" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8">
            {managers.map(m => (
              <PortraitCard key={m.id} member={m} />
            ))}
          </div>
        </motion.div>

        {/* Detail dialog */}
        <Dialog open={!!selectedMember} onOpenChange={(open) => !open && setSelectedMember(null)}>
          <DialogContent
            className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 border border-[hsl(var(--hairline))]"
            style={{ backgroundColor: '#FFFFFF' }}
          >
            {selectedMember && (
              <div className="grid grid-cols-1 md:grid-cols-5" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="md:col-span-2" style={{ backgroundColor: '#F4F4F4' }}>
                  <div className="aspect-[4/5] overflow-hidden">
                    {selectedMember.imageUrl && !imageErrors.has(selectedMember.id) ? (
                      <img
                        src={selectedMember.imageUrl}
                        alt={selectedMember.name}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: selectedMember.imagePosition ?? 'center' }}
                        onError={() => setImageErrors(prev => new Set(prev).add(selectedMember.id))}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-24 h-24" style={{ color: '#21394F', opacity: 0.3 }} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="md:col-span-3 p-10 md:p-14" style={{ backgroundColor: '#FFFFFF' }}>
                  <DialogHeader className="space-y-2 mb-6 text-left">
                    <span
                      className="font-display text-xs font-medium tabular-nums"
                      style={{ letterSpacing: '0.2em', color: '#6E7785' }}
                    >
                      — {t('sub.leadership')}
                    </span>
                    <DialogTitle
                      className="font-display text-3xl md:text-4xl font-bold tracking-tight"
                      style={{ color: '#21394F' }}
                    >
                      {selectedMember.name}
                    </DialogTitle>
                    <DialogDescription
                      className="font-display text-base uppercase tracking-[0.18em]"
                      style={{ color: '#949C58' }}
                    >
                      {selectedMember.position}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="h-px w-full mb-6" style={{ backgroundColor: '#E4E4E4' }} />
                  <p
                    className="leading-relaxed whitespace-pre-line text-base"
                    style={{ color: '#21394F' }}
                  >
                    {selectedMember.descriptionKey
                      ? t(selectedMember.descriptionKey)
                      : selectedMember.description || `Informácie o ${selectedMember.name} budú čoskoro doplnené.`}
                  </p>
                  {selectedMember.linkedinUrl && (
                    <div className="mt-8 pt-8" style={{ borderTop: '1px solid #E4E4E4' }}>
                      <a
                        href={selectedMember.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-display text-sm font-semibold transition-colors hover:opacity-80"
                        style={{ color: '#21394F' }}
                      >
                        <Linkedin className="w-4 h-4" /> LinkedIn Profile <ArrowUpRight className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default Leadership;
